import { apiClient } from './api'

/**
 * Group chat service. Backed by server/routes/groups.js message endpoints.
 */
export const groupsService = {
  /**
   * Create a group; returns the created group (UI shape). `source:
   * 'housemates'` marks one made from a housemate match (outcome log).
   */
  async create({ name, description, maxMembers, source }) {
    const res = await apiClient.post('/groups', {
      name,
      description,
      maxMembers,
      ...(source && { source }),
    })
    return res.group
  },

  /**
   * Public: the household invite behind /household/:token (no session
   * needed; a signed-in viewer is told whether it is theirs).
   */
  async getInvite(token) {
    const res = await apiClient.get(`/groups/invite/${token}`)
    return res.invite
  },

  /**
   * Join through the invite: the signed-in renter on the invited email, an
   * existing account's `{ password }`, or a new account's `{ firstName,
   * lastName, password, acceptedTerms: true }`. Returns `{ token, groupId,
   * created }`.
   */
  async acceptInvite(token, payload = {}) {
    return apiClient.post(`/groups/invite/${token}/accept`, payload)
  },

  /** The household wishlist: places with votes and the per-seat split. */
  async getWishlist(groupId) {
    const res = await apiClient.get(`/groups/${groupId}/listings`)
    return res.listings || []
  },

  /** Add a place to the wishlist (the same place again changes nothing). */
  async addToWishlist(groupId, listingId) {
    const res = await apiClient.post(`/groups/${groupId}/listings`, {
      listingId,
    })
    return res.listing
  },

  /** Yes (1), No (-1), or take your vote back (0). */
  async voteOnWishlist(groupId, wishlistId, value) {
    const res = await apiClient.post(
      `/groups/${groupId}/listings/${wishlistId}/vote`,
      { value }
    )
    return res.listing
  },

  /** The members' plain-text note on how the rent might split. */
  async setWishlistNote(groupId, wishlistId, splitNote) {
    const res = await apiClient.put(
      `/groups/${groupId}/listings/${wishlistId}`,
      { splitNote }
    )
    return res.listing
  },

  /** Take a place off the wishlist (who added it, or an admin). */
  async removeFromWishlist(groupId, wishlistId) {
    return apiClient.delete(`/groups/${groupId}/listings/${wishlistId}`)
  },

  /** The household page: seats, members, requests (admins), listing. */
  async getHousehold(groupId) {
    const res = await apiClient.get(`/groups/${groupId}/household`)
    return res.household
  },

  /** Admin: set month, city, areas, rent per seat, open seats, discoverable. */
  async updateHousehold(groupId, data) {
    const res = await apiClient.put(`/groups/${groupId}/household`, data)
    return res.group
  },

  /** Ask to join a discoverable household, with a short note. */
  async requestToJoin(groupId, note) {
    const res = await apiClient.post(`/groups/${groupId}/join-request`, {
      note,
    })
    return res.request
  },

  /** Admin: 'accept' or 'decline' a join request. */
  async answerRequest(groupId, memberId, decision) {
    return apiClient.post(`/groups/${groupId}/requests/${memberId}/${decision}`)
  },

  /** Vote on a join request: 'approve' or 'decline' (any active member). */
  async voteOnRequest(groupId, memberId, value) {
    return apiClient.post(`/groups/${groupId}/requests/${memberId}/vote`, {
      value,
    })
  },

  // ---- Group listings ("List our group", 2026-10-23) --------------------

  /**
   * Step one of "List our group": `{ groups, people, inviteLinks,
   * hasActiveProfile }`: the households you run and the people you could
   * bring in (mutual Interested or a housemate conversation; no emails).
   */
  async getListCandidates() {
    return apiClient.get('/groups/list/candidates')
  },

  /**
   * Publish a group listing. `{ groupId?, name?, description, openSeats,
   * moveInMonth, city, areas, rentPerSeatMin, rentPerSeatMax, shareWith,
   * joinRule, userIds, emails }`. Returns `{ group: { id }, invited,
   * waitingOn, shareWithApplied }`.
   */
  async publishListing(payload) {
    return apiClient.post('/groups/list', payload)
  },

  /**
   * Start a household (one flow for every way in). `{ name?, description?,
   * userIds, emails, openSeats (0-3), moveInMonth, city, areas,
   * rentPerSeatMin, rentPerSeatMax, discoverable }`. Returns `{ group: { id },
   * invited, waitingOn, shareWithApplied, listed }`.
   */
  async startHousehold(payload) {
    return apiClient.post('/groups/start', payload)
  },

  /**
   * What "Find my replacement" would start from a lease, without making it:
   * `{ preview: { name, listingId, moveInMonth, city, areas, rentPerSeatMin,
   * rentPerSeatMax, openSeats, stayers }, existingId }`.
   */
  async fromLeasePreview(agreementId) {
    return apiClient.get('/groups/from-lease/preview', {
      params: { agreementId },
    })
  },

  /** "Show me in this group listing?": true (yes) or false (no / withdraw). */
  async setListingConsent(groupId, agree) {
    return apiClient.post(`/groups/${groupId}/listing-consent`, { agree })
  },

  /** "Meet the house": `{ slots: [iso], kind, note?, requestMemberId? }`. */
  async proposeMeeting(groupId, payload) {
    const res = await apiClient.post(`/groups/${groupId}/meetings`, payload)
    return res.meeting
  },

  /** Pick one of the suggested times. */
  async chooseMeeting(groupId, meetingId, slot) {
    const res = await apiClient.post(
      `/groups/${groupId}/meetings/${meetingId}/choose`,
      { slot }
    )
    return res.meeting
  },

  /** Propose that household `otherGroupId` join household `groupId`. */
  async proposeMerge(groupId, otherGroupId) {
    const res = await apiClient.post(`/groups/${groupId}/merge-proposals`, {
      otherGroupId,
    })
    return res.proposal
  },

  /** Vote on a merge: 'approve' or 'decline'. */
  async voteOnMerge(proposalId, value) {
    return apiClient.post(`/groups/merge-proposals/${proposalId}/vote`, {
      value,
    })
  },

  /**
   * "Find my replacement": the household with one open seat on this lease
   * (made once). Returns `{ group: { id }, existing }`.
   */
  async fromLease(agreementId) {
    return apiClient.post('/groups/from-lease', { agreementId })
  },

  /** Groups the current user belongs to. */
  async listMy() {
    const res = await apiClient.get('/groups/my')
    return res.groups || []
  },

  /** Group detail (members only). */
  async getById(groupId) {
    const res = await apiClient.get(`/groups/${groupId}`)
    return res.group
  },

  /** Admin: invite a member by email. Returns the created member row. */
  async invite(groupId, email) {
    const res = await apiClient.post(`/groups/${groupId}/invite`, { email })
    return res.member
  },

  /** Admin: invite a Rentra user by id (no email exposed). */
  async inviteUser(groupId, userId) {
    const res = await apiClient.post(`/groups/${groupId}/invite`, { userId })
    return res.member
  },

  /** Pending invitations for the current user. */
  async listInvitations() {
    const res = await apiClient.get('/groups/invitations')
    return res.invitations || []
  },

  /** Accept an invitation; returns the joined group (UI shape). */
  async join(groupId) {
    const res = await apiClient.post(`/groups/${groupId}/join`)
    return res.group
  },

  /** Decline an invitation. */
  async decline(groupId) {
    return apiClient.post(`/groups/${groupId}/decline`)
  },

  /** Remove a member (admin) or leave the group (own member row). */
  async removeMember(groupId, memberId) {
    return apiClient.delete(`/groups/${groupId}/members/${memberId}`)
  },

  /** Creator: delete the group. */
  async deleteGroup(groupId) {
    return apiClient.delete(`/groups/${groupId}`)
  },

  /** Fetch a group's chat history. */
  async getMessages(groupId) {
    const res = await apiClient.get(`/groups/${groupId}/messages`)
    return res.messages || []
  },

  /**
   * Send a chat message; returns the created message. Pass
   * `{ type: 'listing', metadata: { listingData } }` to share a listing
   * card into the chat.
   */
  async sendMessage(groupId, content, { type, metadata } = {}) {
    const res = await apiClient.post(`/groups/${groupId}/messages`, {
      content,
      ...(type && { type }),
      ...(metadata && { metadata }),
    })
    return res.message
  },

  /**
   * Delete a message you wrote in a household chat.
   * @param {string} groupId
   * @param {string} messageId
   */
  async deleteMessage(groupId, messageId) {
    return apiClient.delete(`/groups/${groupId}/messages/${messageId}`)
  },
}

export default groupsService
